/**
 * BhiHero.jsx — iter846 (Feb 2026)
 *
 * Extracted from BreastHealthIntelligencePage.jsx. Renders the top of the
 * BHI page: gradient title, subtitle, doctor attribution banner, and the
 * cluster of header action buttons (Personal View toggle, Tour launcher,
 * Getting Started, Audit, Methodology, Export PDF).
 *
 * Kept as a "dumb" component — all state (gsOpen, auditOpen) lives in the
 * parent so modals stay wired to existing state.
 */
import React from "react";
import { Link } from "react-router-dom";
import { HeartPulse, Stethoscope, ExternalLink, MapPin, ShieldAlert, BookOpen, Printer, ChevronDown, ChevronUp, Target } from "lucide-react";
import { BhiTourLauncher } from "./BhiGuidedTour";
import { expandAllSections, collapseAllSections, markPersonalViewSeen } from "./BhiSection";
import EducationalRibbon from "@/components/EducationalRibbon";
import BhiOnboardingMiniProgress from "./BhiOnboardingMiniProgress";
import BhiHeroStrips from "./BhiHeroStrips";

// iter847 — Small internal Personal-View toggle. Was previously passed
// as a component prop from the parent; now built-in for cleaner API.
function InternalPersonalViewToggle({ isDark, lang }) {
  const [allExpanded, setAllExpanded] = React.useState(false);
  React.useEffect(() => { markPersonalViewSeen(); }, []);
  const toggle = () => {
    if (allExpanded) { collapseAllSections(); setAllExpanded(false); }
    else             { expandAllSections();   setAllExpanded(true); }
  };
  return (
    <button
      type="button"
      onClick={toggle}
      data-testid="bhi-personal-view-toggle"
      className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
        isDark
          ? "border-cyan-500/40 bg-cyan-500/10 text-cyan-300 hover:bg-cyan-500/20"
          : "border-cyan-300 bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
      }`}
      title={
        allExpanded
          ? (lang === "bg" ? "Свий всички секции" : "Collapse all sections")
          : (lang === "bg" ? "Разгъни всички секции — пълен експертен изглед" : "Expand all sections")
      }
    >
      {allExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      {allExpanded
        ? (lang === "bg" ? "Свий всички" : "Collapse all")
        : (lang === "bg" ? "Разгъни всички" : "Expand all")}
    </button>
  );
}

export default function BhiHero({
  isDark,
  lang,
  t,
  user,
  onGettingStarted,
  onAudit,
  onSmartExpand,
  smartExpandCount = 0,
  onOpenClinicalPdf,
}) {
  return (
    <header className="mt-4 mb-6">
      <EducationalRibbon
        lang={lang}
        variant="banner"
        isDark={isDark}
        className="mb-4"
        testId="bhi-educational-ribbon"
      />
      <div className="flex flex-col items-start gap-4">
        {/* Row 1 — icon + title */}
        <div className="flex items-start gap-4 w-full">
          {/* Icon badge */}
          <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
            isDark ? "bg-pink-500/20" : "bg-pink-100"
          }`}>
            <HeartPulse className={`w-7 h-7 ${isDark ? "text-pink-300" : "text-pink-600"}`} />
          </div>

          {/* Title + subtitle + author credit */}
          <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-extrabold leading-tight bg-gradient-to-r from-pink-500 via-rose-500 to-purple-500 bg-clip-text text-transparent">
            {t.title}
          </h1>
          <p className={`text-sm sm:text-base mt-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
            {t.subtitle}
          </p>
          {/* iter839 — Doctor attribution. iter1038 — mobile-friendly: block on <sm to avoid pill-text wrapping. */}
          <div
            className={`mt-3 flex flex-wrap items-center gap-1.5 px-3 py-1.5 rounded-xl sm:rounded-full text-[11px] font-semibold ${
              isDark ? "bg-pink-500/10 text-pink-200 border border-pink-500/30" : "bg-pink-50 text-pink-700 border border-pink-200"
            }`}
            data-testid="bhi-author-credit"
          >
            <Stethoscope className="w-3.5 h-3.5 shrink-0" />
            <span className="shrink-0">Разработено с</span>
            <a
              href="https://superdoc.bg/lekar/ivan-inkov"
              target="_blank"
              rel="noopener noreferrer"
              className="underline decoration-dotted underline-offset-2 hover:decoration-solid inline-flex items-center gap-1 flex-wrap"
              data-testid="bhi-author-link"
            >
              <span>Д-р Иван Инков</span>
              <span className="opacity-70">· Гръден хирург, Мамолог · София</span>
              <ExternalLink className="w-2.5 h-2.5 opacity-70 shrink-0" />
            </a>
          </div>
          {/* iter1040 — Contextual hero strips (social proof, BC subtype, Prevention progress, Top-3 Twins) */}
          <BhiHeroStrips isDark={isDark} />
        </div>
        </div>  {/* end Row 1 (icon + title/subtitle) */}

        {/* Header controls — iter1036 · опростени. Премахнати: Разгъни моите приоритети,
            BhiTourLauncher (Обиколка), Клиничен PDF, Печатен изглед — намаляват
            когнитивното натоварване в началото. Кликвания са в Sidebar / Optional. */}
        {user ? (
          <div className="flex flex-wrap items-center gap-2 w-full">
            {/* iter1038 — Onboarding mini progress (10-step visible in hero) */}
            <BhiOnboardingMiniProgress onOpen={onGettingStarted} isDark={isDark} />
            <InternalPersonalViewToggle isDark={isDark} lang={lang} />
            <button
              type="button"
              onClick={onGettingStarted}
              data-testid="bhi-getting-started-btn"
              className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-pink-500 to-purple-500 px-3 py-2 text-xs font-semibold text-white shadow-sm transition hover:from-pink-600 hover:to-purple-600"
              title={lang === "bg" ? "Откъде да започна — 30-дневен план за нови пациенти" : "Getting started guide"}
            >
              <MapPin className="w-4 h-4" />
              {lang === "bg" ? "Откъде да започна" : "Getting started"}
            </button>
            <button
              type="button"
              onClick={onAudit}
              data-testid="bhi-audit-btn"
              className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-semibold transition ${
                isDark
                  ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
                  : "border-emerald-300 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
              }`}
              title={lang === "bg" ? "Одит на данните — всичките сигнали използвани в snapshot-а" : "Signal audit"}
            >
              <ShieldAlert className="w-4 h-4" />
              {lang === "bg" ? "Одит на данните" : "Signal audit"}
            </button>
          </div>
        ) : null}
      </div>
    </header>
  );
}
